import { Compass, FileUp, BookOpen, Loader2 } from "lucide-react";
import type { EntryPath } from "@/lib/guideRegistry";

interface Props {
  onDiscover: () => void;
  onImport: () => void;
  onZotero: () => void;
  onSkip: () => void;
  onSelectPath: (path: EntryPath) => void;
  isDiscovering?: boolean;
}

interface PathCard {
  path: EntryPath;
  label: string;
  description: string;
  detail: string;
  icon: React.ReactNode;
  colorClasses: {
    border: string;
    iconBg: string;
    iconText: string;
    hover: string;
  };
  handler: () => void;
}

export function CorpusSeedingPanel({
  onDiscover,
  onImport,
  onZotero,
  onSkip,
  onSelectPath,
  isDiscovering = false,
}: Props) {
  const cards: PathCard[] = [
    {
      path: "discover",
      label: "Find papers for me",
      description: "Automatic discovery",
      detail: "PaperChase searches Semantic Scholar to find seed papers for your concept.",
      icon: <Compass className="size-6" />,
      colorClasses: {
        border: "border-green-500/30 hover:border-green-500/60",
        iconBg: "bg-green-500/15",
        iconText: "text-green-400",
        hover: "hover:bg-green-500/5",
      },
      handler: onDiscover,
    },
    {
      path: "import",
      label: "I have papers already",
      description: "PDFs, .bib, or bibliography",
      detail: "Upload PDFs or a BibTeX file to seed your corpus with papers you already know.",
      icon: <FileUp className="size-6" />,
      colorClasses: {
        border: "border-blue-500/30 hover:border-blue-500/60",
        iconBg: "bg-blue-500/15",
        iconText: "text-blue-400",
        hover: "hover:bg-blue-500/5",
      },
      handler: onImport,
    },
    {
      path: "zotero",
      label: "Import from Zotero",
      description: "Organised library",
      detail: "Connect your Zotero library and import papers from a collection.",
      icon: <BookOpen className="size-6" />,
      colorClasses: {
        border: "border-orange-400/30 hover:border-orange-400/60",
        iconBg: "bg-orange-400/15",
        iconText: "text-orange-400",
        hover: "hover:bg-orange-400/5",
      },
      handler: onZotero,
    },
  ];

  return (
    <div className="flex flex-col gap-6 py-4" data-tour-target="corpus-seeding-panel">
      {isDiscovering ? (
        /* Discovery in-progress state */
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-green-500/25 bg-green-500/5 px-8 py-12">
          <Loader2 className="size-8 text-green-400 animate-spin" />
          <div className="text-center">
            <p className="font-medium text-sm">Discovering seed papers...</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Next: trawl from your seeds to find related papers — those are scored automatically.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Prompt */}
          <div className="text-center">
            <p className="font-semibold text-base">How would you like to start building your corpus?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose a path to add your first seed papers.
            </p>
          </div>

          {/* Three path cards */}
          <div className="grid grid-cols-3 gap-4">
            {cards.map((card) => (
              <button
                key={card.path}
                className={[
                  "flex flex-col items-start gap-3 rounded-lg border p-5 text-left transition-colors",
                  card.colorClasses.border,
                  card.colorClasses.hover,
                ].join(" ")}
                onClick={() => {
                  onSelectPath(card.path);
                  card.handler();
                }}
              >
                <div
                  className={[
                    "flex size-10 items-center justify-center rounded-lg",
                    card.colorClasses.iconBg,
                    card.colorClasses.iconText,
                  ].join(" ")}
                >
                  {card.icon}
                </div>
                <div>
                  <p className="font-semibold text-sm">{card.label}</p>
                  <p className="mt-0.5 text-xs font-medium text-muted-foreground">{card.description}</p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{card.detail}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Skip link */}
          <div className="text-center">
            <button
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
              onClick={onSkip}
            >
              I'll add papers manually later
            </button>
          </div>
        </>
      )}
    </div>
  );
}
